import { isWebEnvironment, isReactNativeEnvironment, signalRLog } from './platform';

const ajaxWithFetch = async (headers, options) => {
  try {
    const fetchOptions = {
      method: options.type,
      headers: {
        'Content-Type': options.contentType || 'application/x-www-form-urlencoded; charset=UTF-8',
        ...headers
      },
    };

    if (options.type === 'POST' && options.data) {
      fetchOptions.body = qs(options.data);
    }

    const response = await fetch(options.url, fetchOptions);

    if (response.ok) {
      const text = await response.text();
      if (options.success) {
        try {
          const parsed = JSON.parse(text);
          options.success(parsed);
        } catch (e) {
          signalRLog('JSON parse error with fetch:', e);
          if (options.error) {
            try { options.error(response, e); } catch (handlerErr) { signalRLog('Error in options.error handler:', handlerErr); }
          }
        }
      }
    } else {
      if (options.error) {
        const errorText = await response.text();
        signalRLog('Fetch error response:', errorText);
        try { options.error(response, new Error(`HTTP ${response.status}: ${response.statusText}`)); } catch (handlerErr) { signalRLog('Error in options.error handler:', handlerErr); }
      }
    }
  } catch (error) {
    signalRLog('Fetch error:', error);
    if (options.error) {
      try { options.error(null, error); } catch (handlerErr) { signalRLog('Error in options.error handler:', handlerErr); }
    }
  }
};

let XMLHttpRequestClass;
if (isWebEnvironment) {
  XMLHttpRequestClass = XMLHttpRequest;
} else if (isReactNativeEnvironment) {
  XMLHttpRequestClass = global.XMLHttpRequest || XMLHttpRequest;
}

const qs = data => {
  let results = [];
  for (const name in data)
    results.push(`${name}=${encodeURIComponent(data[name])}`);
  return results.join('&');
};

const ajax = (headers, options) => {
  if (isReactNativeEnvironment && !XMLHttpRequestClass) {
    signalRLog('XMLHttpRequest not available in React Native, using fetch fallback');
    ajaxWithFetch(headers, options);
    return { abort: () => {} };
  }

  const request = new XMLHttpRequestClass();
  let aborted = false;
  let hasTimedOut = false;
  
  // Scale with caller-provided timeout so long polls (~120s) aren't prematurely aborted
  let backupTimeout = null;
  if (isReactNativeEnvironment) {
    const backupTimeoutMs = (options.timeout || 10000) + 5000;
    backupTimeout = setTimeout(() => {
      if (!hasTimedOut && !aborted && request.readyState !== 4) {
        hasTimedOut = true;
        request.abort();
        ajaxWithFetch(headers, options);
      }
    }, backupTimeoutMs);
  }
  
  request.onerror = (error) => {
    signalRLog('XMLHttpRequest onerror:', {
      error,
      status: request.status,
      readyState: request.readyState,
    });
    if (options.error && !aborted) {
      const errorMessage = error?.message || `Network error: ${request.status} ${request.statusText}`;
      try { options.error(request, new Error(errorMessage)); } catch (handlerErr) { signalRLog('Error in options.error handler:', handlerErr); }
    }
  };

  request.ontimeout = () => {
    signalRLog('XMLHttpRequest ontimeout');
    if (options.error && !aborted) {
      try { options.error(request, new Error('Request timeout')); } catch (handlerErr) { signalRLog('Error in options.error handler:', handlerErr); }
    }
  };

  request.onloadend = () => {};

  request.onreadystatechange = () => {
    if (aborted || hasTimedOut) {
      return;
    }

    if (request.readyState !== 4) {
      return;
    }

    if (backupTimeout) {
      clearTimeout(backupTimeout);
      backupTimeout = null;
    }

    if (request.status >= 200 && request.status < 300 && !request._hasError && options.success) {
      try {
        const responseText = request.responseText;
        if (!responseText) {
          throw new Error('Empty response from server');
        }
        const parsed = JSON.parse(responseText);
        options.success(parsed);
      } catch (e) {
        signalRLog('JSON parse error:', e);
        if (options.error) {
          options.error(request, e);
        }
      }
    } else if (options.error) {
      signalRLog('XMLHttpRequest error response:', {
        status: request.status,
        statusText: request.statusText,
      });
      
      let errorMessage = `HTTP ${request.status}`;
      if (request.statusText) {
        errorMessage += `: ${request.statusText}`;
      }
      if (request.status === 0) {
        errorMessage = 'Network error: Unable to connect to server';
      }
      
      const error = request._response || new Error(errorMessage);
      try { options.error(request, error); } catch (handlerErr) { signalRLog('Error in options.error handler:', handlerErr); }
    }
  };

  try {
    request.open(options.type, options.url);
    
    if (isReactNativeEnvironment && request.timeout !== undefined) {
      request.timeout = options.timeout || 10000;
    }
    
    if (options.contentType) {
      request.setRequestHeader('content-type', options.contentType);
    }

    if (headers) {
      Object.keys(headers).forEach(key => {
        const value = headers[key];
        if (value != null) {
          try {
            request.setRequestHeader(key, value);
          } catch (headerError) {
            signalRLog(`Failed to set header ${key}:`, headerError);
          }
        }
      });
    }

    if (isReactNativeEnvironment) {
      try {
        request.withCredentials = true;
      } catch (credError) {
        signalRLog('Failed to set withCredentials:', credError);
      }
    }

    if (options.xhrFields) {
      Object.keys(options.xhrFields).forEach(key => {
        const value = options.xhrFields[key];
        try {
          request[key] = value;
        } catch (fieldError) {
          signalRLog(`Failed to set xhr field ${key}:`, fieldError);
        }
      });
    }

    let dataToSend = undefined;
    if (options.type === 'POST' && options.data) {
      dataToSend = qs(options.data);
    }

    request.send(dataToSend);

  } catch (requestError) {
    signalRLog('XMLHttpRequest setup error:', requestError);
    if (options.error && !aborted) {
      setTimeout(() => { try { options.error(request, requestError); } catch (handlerErr) { signalRLog('Error in options.error handler:', handlerErr); } }, 0);
    }
  }

  return {
    abort: reason => {
      aborted = true;
      if (backupTimeout) {
        clearTimeout(backupTimeout);
        backupTimeout = null;
      }
      return request.abort(reason);
    },
  };
};

export default ajax;
